/**
 * Powered By Slider Component
 * Auto-scrolling partner/technology logos slider
 */

import { useEffect, useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '../../services/api'

// Gold color constant (Brandbook)
const GOLD = '#EFCE92'

interface Partner {
  id: string
  name: string
  logoUrl: string
  websiteUrl?: string
  displayOrder: number
  isActive?: boolean
}

// Default partners removed - partners are managed via Admin Panel and database
const defaultPartners: Partner[] = []

export function PoweredBySlider() {
  const { t } = useTranslation('landing')
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isPaused, setIsPaused] = useState(false)

  // Fetch partners from API
  const { data: partners } = useQuery({
    queryKey: ['partners'],
    queryFn: async () => {
      try {
        const response = await api.getPartners()
        return response.data as Partner[]
      } catch {
        return defaultPartners
      }
    },
    staleTime: 5 * 60 * 1000,
  })

  const displayPartners = partners && partners.length > 0 ? partners : defaultPartners
  const hasPartners = displayPartners && displayPartners.length > 0
  // Need at least 3 partners for smooth scroll animation
  const shouldAnimate = displayPartners.length >= 3

  // Auto-scroll animation (only if enough partners for smooth loop)
  useEffect(() => {
    const scrollContainer = scrollRef.current
    if (!scrollContainer || isPaused || !hasPartners || !shouldAnimate) return

    let animationId: number
    let scrollPosition = 0
    const scrollSpeed = 0.5

    const animate = () => {
      scrollPosition += scrollSpeed
      if (scrollPosition >= scrollContainer.scrollWidth / 2) {
        scrollPosition = 0
      }
      scrollContainer.scrollLeft = scrollPosition
      animationId = requestAnimationFrame(animate)
    }

    animationId = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(animationId)
  }, [isPaused, displayPartners, hasPartners, shouldAnimate])

  // Don't render if no partners (after all hooks)
  if (!hasPartners) {
    return null
  }

  return (
    <section className="py-16 bg-navy-900 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/2 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}></div>
      </div>
      <div className="absolute top-0 left-1/4 w-32 h-32 rounded-full opacity-5" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}></div>
      <div className="absolute bottom-0 right-1/4 w-32 h-32 rounded-full opacity-5" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}></div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            {t('poweredBy.title')}
          </h2>
          <p className="text-gray-400 text-sm">
            {t('poweredBy.subtitle')}
          </p>
        </div>

        {/* Logo slider (if logos are available) */}
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-navy-900 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-navy-900 to-transparent z-10 pointer-events-none"></div>

          <div
            ref={scrollRef}
            className={`py-4 ${shouldAnimate ? 'overflow-hidden' : 'overflow-visible'}`}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className={`flex items-center gap-16 ${shouldAnimate ? 'w-max' : 'justify-center flex-wrap'}`}>
              {displayPartners.map((partner, index) => (
                <a
                  key={`${partner.id}-${index}`}
                  href={partner.websiteUrl || '#'}
                  target={partner.websiteUrl ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="flex-shrink-0 flex items-center justify-center h-20 px-6 rounded-xl border border-gold-300/10 bg-navy-800/50 hover:border-gold-300/30 hover:bg-navy-800 transition-all duration-300 group"
                  title={partner.name}
                >
                  {partner.logoUrl ? (
                    <img
                      src={partner.logoUrl}
                      alt={partner.name}
                      className="max-h-12 max-w-[180px] object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ filter: 'brightness(0) saturate(100%) invert(85%) sepia(18%) saturate(522%) hue-rotate(358deg) brightness(101%) contrast(89%)' }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                        const parent = target.parentElement
                        if (parent) {
                          const span = document.createElement('span')
                          span.textContent = partner.name
                          span.style.color = GOLD
                          span.style.fontWeight = '600'
                          span.style.fontSize = '1rem'
                          parent.appendChild(span)
                        }
                      }}
                    />
                  ) : (
                    <span
                      className="font-semibold text-base"
                      style={{ color: GOLD }}
                    >
                      {partner.name}
                    </span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom decorative line */}
        <div className="mt-8 flex justify-center">
          <div className="w-32 h-1 rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}></div>
        </div>
      </div>
    </section>
  )
}

export default PoweredBySlider
